import axios from "axios";
import { atom, useAtom } from "jotai";
import {
	ApiTaskKanbanStatus,
	ApiTaskKanban,
	ApiPagination,
} from "src/api/types";
import useTaskStatuses from "src/hooks/api/tasks/useTaskStatuses";
import useTasksFilter from "src/hooks/api/tasks/useTasksFilter";
import { createApiInstance } from "src/lib/api";

export interface ActiveDraggableTask {
	id: string | number;
	from: ApiTaskKanbanStatus;
	to: ApiTaskKanbanStatus;
}

type Columns = {
	[key in ApiTaskKanbanStatus]: {
		meta: ApiPagination;
		tasks: ApiTaskKanban[];
		status: "loading" | "idle";
		params?: {
			page?: number;
			q?: string;
		};
	};
};

type State = {
	columns: Columns;
	status: "loading";
};

const tasksKanbanAtom: any = atom({
	columns: {},
	status: "loading",
});

export default function useTasksKanban() {
	const taskStatuses = useTaskStatuses();
	const { filter } = useTasksFilter();
	const [state, setState] = useAtom<State, any, any>(tasksKanbanAtom);
	const api = createApiInstance({
		baseURL: `${axios.defaults.baseURL}/company/tasks`,
	});

	const getAllStatuses = async () => {
		setState((s: any) => ({ ...s, status: "loading" }));
		let statuses: string[] = [];
		if (!taskStatuses.statuses?.length) {
			statuses = await taskStatuses.actions.get();
		} else {
			statuses = taskStatuses.statuses;
		}

		const columns = Object.assign(
			{},
			...statuses.map((status: any) => ({
				[status]: {
					tasks: [],
					status: "idle",
				},
			}))
		);

		setState((s: any) => ({ ...s, columns, status: "idle" }));
		return statuses;
	};

	const getColumn = async (
		status: ApiTaskKanbanStatus,
		params?: { page?: number },
		mode?: "merge" | "reset"
	) => {
		setState((s: State) => ({
			...s,
			columns: {
				...s.columns,
				[status]: {
					...s.columns[status],
					status: "loading",
				},
			},
		}));

		let { data: newTasks, meta } = await api
			.get(`/kanban/${status}`, {
				params: {
					...params,
					...filter,
				},
			})
			.then(({ data }) => data);

		const currentColumn = state.columns[status];

		const column = {
			tasks: currentColumn?.tasks || [],
			meta,
			status: "idle",
			params,
		};

		if (!mode || mode === "merge") {
			//Update the current state with new fetched items
			const currentTasks = (currentColumn?.tasks || []).map((current) => {
				const newTask = newTasks.find(
					(task: any) => task.id === current.id
				);
				return newTask?.id ? newTask : current;
			});

			//Filter out the tasks that are already in state
			if (currentTasks.length) {
				newTasks = newTasks.filter((item: any) => {
					return !currentTasks.find(
						(current) => current.id === item.id
					);
				});
			}
			column.tasks = [...currentTasks, ...newTasks];
		} else {
			column.tasks = newTasks;
		}

		setState((s: State) => ({
			...s,
			columns: {
				...s.columns,
				[status]: column,
			},
		}));

		return column;
	};

	const setStatus = ({ from, to, id }: ActiveDraggableTask) => {
		if (from && to) {
			setState((state: State) => {
				const item = state.columns[from].tasks.find(
					(row: any) => row.id === id
				);
				const columns = state.columns;
				columns[from] = {
					...columns[from],
					tasks: columns[from].tasks.filter((r: any) => r.id !== id),
				};
				if (item) {
					columns[to] = {
						...columns[to],
						tasks: [item, ...columns[to].tasks],
					};
				}

				return {
					...state,
					columns,
				};
			});
		}
	};

	const updateStatus = async (params: ActiveDraggableTask) => {
		if (!params.to) {
			return;
		}

		if (!params.from) params.from = "BACKLOG";

		setStatus(params);
		api.patch(`/${params.id}/status`, {
			status: params.to,
		});
	};

	return {
		atom: tasksKanbanAtom,
		state,
		api,
		actions: {
			getAllStatuses,
			getColumn,
			updateStatus,
			setStatus,
			setState,
		},
	};
}
